import React, {useEffect, useRef, useState} from "react";
import {useNavigate, useOutletContext, useParams, useSearchParams} from "react-router-dom";
import Button from "../../../../../components/Button";
import api from "../../../../../lib/api";
import {notify} from "../../../../../components/Notifications";
import {getErrMsg} from "../../../../../lib/errors";
import {useBusiness} from "../../../../../context/BusinessContext";
import {GripVerticalIcon, PencilIcon, PlusIcon, SaveIcon, ShieldIcon, Trash2Icon} from "lucide-react";
import {MarkdownField} from "../../../../../components/MarkdownField";
import SectionHeading from "../../../../../components/SectionHeading";
import {groupTicketsByKind, TicketScopePicker, type TicketKind, type TicketOption} from "../components/TicketScopePicker";

type OutletCtx = { eventId: string | null };

type CustomFieldForm = {
    id?: string;
    section: string;
    label: string;
    description?: string;
    type: string;
    required: boolean;
    options?: string;
    position?: string;
    ticketTypeIds: string[];
};

type OptionsPayload = {
    collectAttendeeDetailsForEachTicket: boolean;
    collectMedicalAid: boolean;
    medicalAidTicketTypeIds: string[];
    collectEmergencyContact: boolean;
    emergencyContactTicketTypeIds: string[];
    membershipLookupRequired: boolean;
    singleTicketPerOrder: boolean;

    waiverRequired: boolean;
    waiverTicketTypeIds: string[];
    waiverText?: string;

    customFields: CustomFieldForm[];
};

const GROUPS = [
    "CUSTOM",
    "PERSONAL_DETAILS",
    "CONTACT_DETAILS",
    "ADDRESS",
    "MEDICAL_AID",
    "MEDICAL_INFO",
    "EMERGENCY_CONTACT",
    "RACE_DETAILS",
    "REGISTRATION_DETAILS",
    "TEAM_DETAILS",
    "DIETARY_REQUIREMENTS",
    "APPAREL",
    "ACCOMMODATION",
    "TRANSPORT",
    "EXPERIENCE",
    "CONSENT",
    "PAYMENT_DETAILS",
] as const;
const TYPES = ["TEXT", "TEXTAREA", "NUMBER", "DATE", "DROPDOWN", "BOOLEAN", "CHECKBOX", "DOCUMENT", "IMAGE"] as const;

const GROUP_LABELS: Record<(typeof GROUPS)[number], string> = {
    CUSTOM: "Custom",
    PERSONAL_DETAILS: "Personal details",
    CONTACT_DETAILS: "Contact details",
    ADDRESS: "Address",
    MEDICAL_AID: "Medical aid",
    MEDICAL_INFO: "Medical info",
    EMERGENCY_CONTACT: "Emergency contact",
    RACE_DETAILS: "Race details",
    REGISTRATION_DETAILS: "Registration details",
    TEAM_DETAILS: "Team details",
    DIETARY_REQUIREMENTS: "Dietary requirements",
    APPAREL: "Apparel",
    ACCOMMODATION: "Accommodation",
    TRANSPORT: "Transport",
    EXPERIENCE: "Experience",
    CONSENT: "Consent",
    PAYMENT_DETAILS: "Payment details",
};

const TYPE_LABELS: Record<(typeof TYPES)[number], string> = {
    TEXT: "Text",
    TEXTAREA: "Long text",
    NUMBER: "Number",
    DATE: "Date",
    DROPDOWN: "Dropdown",
    BOOLEAN: "Yes / No",
    CHECKBOX: "Checkbox",
    DOCUMENT: "File upload",
    IMAGE: "Image upload",
};

function groupLabel(value: string): string {
    return (GROUP_LABELS as Record<string, string>)[value] ?? value;
}

function typeLabel(value: string): string {
    return (TYPE_LABELS as Record<string, string>)[value] ?? value;
}

function moveItem<T>(arr: T[], from: number, to: number): T[] {
    const copy = [...arr];
    const [x] = copy.splice(from, 1);
    copy.splice(to, 0, x);
    return copy;
}

function normalizePositions(list: CustomFieldForm[]): CustomFieldForm[] {
    return list.map((f, idx) => ({...f, position: String(idx + 1)}));
}

function emptyField(): CustomFieldForm {
    return {
        section: "CUSTOM",
        label: "",
        description: "",
        type: "TEXT",
        required: true,
        options: "",
        position: "",
        ticketTypeIds: [],
    };
}

function ToggleRow({
                       title, hint, checked, onChange, icon,
                   }: {
    title: string;
    hint?: string;
    checked: boolean;
    onChange: (v: boolean) => void;
    icon?: React.ReactNode;
}) {
    return (
        <label
            className="flex items-start justify-between gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-start gap-3">
                {icon ? <div className="mt-0.5 text-gray-500">{icon}</div> : null}
                <div>
                    <div className="text-sm font-semibold text-gray-900">{title}</div>
                    {hint ? <div className="mt-0.5 text-xs text-gray-500">{hint}</div> : null}
                </div>
            </div>
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                className="mt-1 size-4 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
            />
        </label>
    );
}

export function DataCollectionStep({mode}: { mode: "create" | "edit" }) {
    const {activeBusiness} = useBusiness();
    const businessId = activeBusiness?.id ?? null;

    const navigate = useNavigate();
    const params = useParams();
    const [sp] = useSearchParams();
    const outlet = useOutletContext<OutletCtx>();

    const eventId =
        mode === "edit"
            ? (params.eventId ?? outlet.eventId ?? null)
            : (outlet.eventId ?? sp.get("eventId") ?? null);

    const [loading, setLoading] = useState(false);

    // separate saving flags
    const [savingOptions, setSavingOptions] = useState(false);
    const [savingField, setSavingField] = useState(false);
    const [savingOrder, setSavingOrder] = useState(false);

    const [opts, setOpts] = useState<OptionsPayload>({
        collectAttendeeDetailsForEachTicket: false,
        collectMedicalAid: false,
        medicalAidTicketTypeIds: [],
        collectEmergencyContact: false,
        emergencyContactTicketTypeIds: [],
        membershipLookupRequired: false,
        singleTicketPerOrder: false,
        waiverRequired: false,
        waiverTicketTypeIds: [],
        waiverText: "",
        customFields: [],
    });

    const [fieldEditor, setFieldEditor] = useState<CustomFieldForm | null>(null);
    const [dirtyOrder, setDirtyOrder] = useState(false);
    const [ticketOptions, setTicketOptions] = useState<TicketOption[]>([]);

    const dragFrom = useRef<number | null>(null);

    // ---- load
    useEffect(() => {
        if (!businessId || !eventId) return;

        setLoading(true);
        (async () => {
            try {
                const {data} = await api.get(`/api/v1/business/${businessId}/events/${eventId}/data-collection`);

                const fields = normalizePositions(
                    (data?.customFields ?? []).map((f: any) => ({
                        id: f?.id ? String(f.id) : undefined,
                        section: f?.section ?? "CUSTOM",
                        label: f?.label ?? "",
                        description: f?.description ?? "",
                        type: f?.type ?? "TEXT",
                        required: Boolean(f?.required ?? false),
                        options: f?.options ?? "",
                        position: f?.position != null ? String(f.position) : "0",
                        ticketTypeIds: Array.isArray(f?.ticketTypeIds)
                            ? f.ticketTypeIds.map((x: any) => String(x))
                            : [],
                    }))
                );

                try {
                    const tRes = await api.get(`/api/v1/business/${businessId}/events/${eventId}/tickets`);
                    const raw = Array.isArray(tRes?.data) ? tRes.data : (tRes?.data?.tickets ?? []);
                    setTicketOptions(
                        (raw ?? [])
                            .slice()
                            .sort((a: any, b: any) => (a?.position ?? 0) - (b?.position ?? 0))
                            .map((t: any) => ({
                                id: String(t?.id ?? ""),
                                name: t?.name ?? "Untitled",
                                kind: (t?.kind ?? "TICKET") as TicketKind,
                            }))
                            .filter((t: TicketOption) => t.id)
                    );
                } catch {
                    setTicketOptions([]);
                }

                const toIdArray = (v: any): string[] =>
                    Array.isArray(v) ? v.map((x: any) => String(x)) : [];

                setOpts({
                    collectAttendeeDetailsForEachTicket: Boolean(data?.collectAttendeeDetailsForEachTicket ?? false),
                    collectMedicalAid: Boolean(data?.collectMedicalAid ?? false),
                    medicalAidTicketTypeIds: toIdArray(data?.medicalAidTicketTypeIds),
                    collectEmergencyContact: Boolean(data?.collectEmergencyContact ?? false),
                    emergencyContactTicketTypeIds: toIdArray(data?.emergencyContactTicketTypeIds),
                    membershipLookupRequired: Boolean(data?.membershipLookupRequired ?? false),
                    singleTicketPerOrder: Boolean(data?.singleTicketPerOrder ?? false),
                    waiverRequired: Boolean(data?.waiverRequired ?? false),
                    waiverTicketTypeIds: toIdArray(data?.waiverTicketTypeIds),
                    waiverText: data?.waiverText ?? "",
                    customFields: fields,
                });

                setDirtyOrder(false);
            } catch (e) {
                notify.error(getErrMsg(e, "Failed to load data collection settings"));
            } finally {
                setLoading(false);
            }
        })();
    }, [businessId, eventId]);

    // ---- options save (debounced)
    const optionsSaveTimer = useRef<number | null>(null);

    const saveOptions = (next: Partial<OptionsPayload>) => {
        if (!businessId || !eventId) return;

        // Optimistic UI
        setOpts((p) => ({...p, ...next}));

        // Debounce PATCH
        if (optionsSaveTimer.current) window.clearTimeout(optionsSaveTimer.current);
        optionsSaveTimer.current = window.setTimeout(async () => {
            setSavingOptions(true);
            try {
                const current = ((): any => {
                    const merged = {...opts, ...next};
                    return {
                        collectAttendeeDetailsForEachTicket: Boolean(merged.collectAttendeeDetailsForEachTicket),
                        collectMedicalAid: Boolean(merged.collectMedicalAid),
                        medicalAidTicketTypeIds: merged.collectMedicalAid ? (merged.medicalAidTicketTypeIds ?? []) : [],
                        collectEmergencyContact: Boolean(merged.collectEmergencyContact),
                        emergencyContactTicketTypeIds: merged.collectEmergencyContact ? (merged.emergencyContactTicketTypeIds ?? []) : [],
                        membershipLookupRequired: Boolean(merged.membershipLookupRequired),
                        singleTicketPerOrder: Boolean(merged.singleTicketPerOrder),
                        waiverRequired: Boolean(merged.waiverRequired),
                        waiverTicketTypeIds: merged.waiverRequired ? (merged.waiverTicketTypeIds ?? []) : [],
                        waiverText: merged.waiverRequired ? (merged.waiverText ?? "") : "",
                    };
                })();

                await api.patch(
                    `/api/v1/business/${businessId}/events/${eventId}/data-collection/options`,
                    current
                );

                notify.success("Options saved");
            } catch (e) {
                notify.error(getErrMsg(e, "Failed to save options"));
            } finally {
                setSavingOptions(false);
            }
        }, 450);
    };

    // ---- fields CRUD
    const openNewField = () => setFieldEditor(emptyField());
    const openEditField = (f: CustomFieldForm) => setFieldEditor({...f});

    const deleteField = async (f: CustomFieldForm) => {
        if (!businessId || !eventId) return;

        if (!f.id) {
            setOpts((prev) => ({...prev, customFields: normalizePositions(prev.customFields.filter((x) => x !== f))}));
            setDirtyOrder(true);
            return;
        }

        if (!confirm(`Delete field "${f.label}"?`)) return;

        try {
            await api.delete(`/api/v1/business/${businessId}/events/${eventId}/custom-fields/${f.id}`);
            setOpts((prev) => ({
                ...prev,
                customFields: normalizePositions(prev.customFields.filter((x) => x.id !== f.id))
            }));
            setDirtyOrder(true);
            notify.success("Field deleted");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to delete field"));
        }
    };

    const upsertField = async (f: CustomFieldForm) => {
        if (!businessId || !eventId) return;

        setSavingField(true);
        try {
            const payload = {
                id: f.id ?? null,
                section: f.section,
                label: f.label.trim(),
                description: f.description ?? "",
                type: f.type,
                required: Boolean(f.required),
                options: f.type === "DROPDOWN" ? (f.options ?? "") : "",
                position: f.position ?? null,
                ticketTypeIds: f.ticketTypeIds ?? [],
            };

            const {data} = await api.post(
                `/api/v1/business/${businessId}/events/${eventId}/custom-fields`,
                payload
            );

            // backend should return the saved field (recommended). If not, at least return {id}
            const savedId = String(data?.id ?? payload.id ?? "");

            setOpts((prev) => {
                const list = prev.customFields;
                const next =
                    f.id
                        ? list.map((x) => (x.id === f.id ? {...x, ...f} : x))
                        : [...list, {...f, id: savedId}];

                return {...prev, customFields: normalizePositions(next)};
            });

            setDirtyOrder(true); // list changed
            notify.success("Field saved");
            setFieldEditor(null);
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to save field"));
        } finally {
            setSavingField(false);
        }
    };

    // ---- reorder save (positions only)
    const saveOrder = async () => {
        if (!businessId || !eventId) return;

        const normalized = normalizePositions(opts.customFields);
        const payload = normalized
            .filter((f) => !!f.id)
            .map((f) => ({id: f.id, position: Number(f.position ?? 0)}));

        setSavingOrder(true);
        try {
            const {data} = await api.patch(
                `/api/v1/business/${businessId}/events/${eventId}/custom-fields/reorder`,
                payload
            );

            const raw = Array.isArray(data) ? data : (data?.customFields ?? data ?? []);
            const mapped = normalizePositions((raw ?? []).map((f: any) => ({
                id: f?.id ? String(f.id) : undefined,
                section: f?.section ?? "CUSTOM",
                label: f?.label ?? "",
                description: f?.description ?? "",
                type: f?.type ?? "TEXT",
                required: Boolean(f?.required ?? false),
                options: f?.options ?? "",
                position: f?.position != null ? String(f.position) : "0",
                ticketTypeIds: Array.isArray(f?.ticketTypeIds)
                    ? f.ticketTypeIds.map((x: any) => String(x))
                    : [],
            })));

            setOpts((p) => ({...p, customFields: mapped}));
            setDirtyOrder(false);
            notify.success("Order saved");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to save field order"));
        } finally {
            setSavingOrder(false);
        }
    };

    // DnD
    const onDragStart = (idx: number) => {
        dragFrom.current = idx;
    };
    const onDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };
    const onDrop = (toIdx: number) => {
        const fromIdx = dragFrom.current;
        dragFrom.current = null;
        if (fromIdx == null || fromIdx === toIdx) return;

        setOpts((prev) => ({...prev, customFields: normalizePositions(moveItem(prev.customFields, fromIdx, toIdx))}));
        setDirtyOrder(true);
    };

    const goNext = () => {
        if (!eventId) return;
        if (mode === "edit") navigate(`/business/events/${encodeURIComponent(eventId)}/edit/coupons`);
        else navigate(`/business/events/new/coupons?eventId=${encodeURIComponent(eventId)}`);
    };

    if (!eventId) {
        return (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
                Please complete <span className="font-medium">Event Details</span> first.
            </div>
        );
    }

    // ---- Field editor view
    if (fieldEditor) {
        const needsOptions = fieldEditor.type === "DROPDOWN";
        const canSave = fieldEditor.label.trim().length > 0 && !savingField;

        return (
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">{fieldEditor.id ? "Edit field" : "New field"}</h2>
                        <p className="mt-1 text-sm text-gray-600">Define what customers must provide during checkout /
                            registration.</p>
                    </div>
                    <div className="flex gap-2">
                        <Button type="button" variant="secondary" size="md" onClick={() => setFieldEditor(null)}
                                disabled={savingField}>
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="primary"
                            size="md"
                            onClick={() => upsertField(fieldEditor)}
                            disabled={!canSave}
                        >
              <span className="inline-flex items-center gap-2">
                <SaveIcon className="size-4"/>{savingField ? "Saving…" : "Save field"}
              </span>
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div>
                        <div className="text-sm font-medium text-gray-900">Group</div>
                        <select
                            value={fieldEditor.section}
                            onChange={(e) => setFieldEditor({...fieldEditor, section: e.target.value})}
                            className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                        >
                            {GROUPS.map((g) => <option key={g} value={g}>{groupLabel(g)}</option>)}
                        </select>
                    </div>

                    <div className="md:col-span-2">
                        <div className="text-sm font-medium text-gray-900">Label *</div>
                        <input
                            value={fieldEditor.label}
                            onChange={(e) => setFieldEditor({...fieldEditor, label: e.target.value})}
                            placeholder="Name of field (e.g. Full name)"
                            className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                        />
                    </div>

                    <div>
                        <div className="text-sm font-medium text-gray-900">Type</div>
                        <select
                            value={fieldEditor.type}
                            onChange={(e) => setFieldEditor({...fieldEditor, type: e.target.value})}
                            className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                        >
                            {TYPES.map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
                        </select>
                    </div>

                    <div className="md:col-span-2">
                        <div className="text-sm font-medium text-gray-900">Description</div>
                        <input
                            value={fieldEditor.description ?? ""}
                            onChange={(e) => setFieldEditor({...fieldEditor, description: e.target.value})}
                            placeholder="Shown under the field (optional)"
                            className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                        />
                    </div>

                    <div
                        className="md:col-span-3 flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 p-4">
                        <div>
                            <div className="text-sm font-semibold text-gray-900">Required</div>
                            <div className="text-xs text-gray-600">Customers must fill this in to proceed.</div>
                        </div>
                        <input
                            type="checkbox"
                            checked={fieldEditor.required}
                            onChange={(e) => setFieldEditor({...fieldEditor, required: e.target.checked})}
                            className="size-4 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
                        />
                    </div>

                    {needsOptions ? (
                        <div className="md:col-span-3">
                            <div className="text-sm font-medium text-gray-900">Options</div>
                            <div className="mt-1 text-xs text-gray-500">Comma-separated values (e.g. Yes, No, Maybe)
                            </div>
                            <input
                                value={fieldEditor.options ?? ""}
                                onChange={(e) => setFieldEditor({...fieldEditor, options: e.target.value})}
                                placeholder="e.g. VIP, Standard, Kids"
                                className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                            />
                        </div>
                    ) : null}

                    <div className="md:col-span-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
                        <div className="flex items-center justify-between gap-4">
                            <div>
                                <div className="text-sm font-semibold text-gray-900">Applies to all tickets</div>
                                <div className="text-xs text-gray-600">
                                    Turn off to restrict this field to specific ticket types.
                                </div>
                            </div>
                            <input
                                type="checkbox"
                                checked={fieldEditor.ticketTypeIds.length === 0}
                                onChange={(e) =>
                                    setFieldEditor({
                                        ...fieldEditor,
                                        ticketTypeIds: e.target.checked ? [] : ticketOptions.map((t) => t.id),
                                    })
                                }
                                className="size-4 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
                            />
                        </div>

                        {fieldEditor.ticketTypeIds.length > 0 ? (
                            <div className="mt-4 space-y-3">
                                {ticketOptions.length === 0 ? (
                                    <div className="text-xs text-gray-500">
                                        No tickets configured yet. Add tickets in the previous step.
                                    </div>
                                ) : (
                                    groupTicketsByKind(ticketOptions).map((group) => {
                                        const groupIds = group.items.map((t) => t.id);
                                        const allSelected = groupIds.every((id) => fieldEditor.ticketTypeIds.includes(id));
                                        const noneSelected = groupIds.every((id) => !fieldEditor.ticketTypeIds.includes(id));
                                        return (
                                        <div key={group.kind} className="space-y-2">
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                                    {group.label}
                                                </div>
                                                <div className="flex items-center gap-2 text-[11px] font-medium">
                                                    <button
                                                        type="button"
                                                        disabled={allSelected}
                                                        onClick={() =>
                                                            setFieldEditor({
                                                                ...fieldEditor,
                                                                ticketTypeIds: [
                                                                    ...fieldEditor.ticketTypeIds,
                                                                    ...groupIds.filter((id) => !fieldEditor.ticketTypeIds.includes(id)),
                                                                ],
                                                            })
                                                        }
                                                        className="text-orange-700 hover:text-orange-800 disabled:text-gray-300 disabled:cursor-default"
                                                    >
                                                        Select all
                                                    </button>
                                                    <span className="text-gray-300">·</span>
                                                    <button
                                                        type="button"
                                                        disabled={noneSelected}
                                                        onClick={() =>
                                                            setFieldEditor({
                                                                ...fieldEditor,
                                                                ticketTypeIds: fieldEditor.ticketTypeIds.filter((id) => !groupIds.includes(id)),
                                                            })
                                                        }
                                                        className="text-gray-500 hover:text-gray-700 disabled:text-gray-300 disabled:cursor-default"
                                                    >
                                                        Deselect all
                                                    </button>
                                                </div>
                                            </div>
                                            {group.items.map((t) => {
                                                const checked = fieldEditor.ticketTypeIds.includes(t.id);
                                                return (
                                                    <label key={t.id} className="flex items-center gap-3">
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={(e) => {
                                                                const next = e.target.checked
                                                                    ? [...fieldEditor.ticketTypeIds, t.id]
                                                                    : fieldEditor.ticketTypeIds.filter((id) => id !== t.id);
                                                                setFieldEditor({...fieldEditor, ticketTypeIds: next});
                                                            }}
                                                            className="size-4 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
                                                        />
                                                        <span className="text-sm text-gray-900">{t.name}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                        );
                                    })
                                )}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        );
    }

    // ---- main view
    return (
        <div className="space-y-6">

            <SectionHeading
                title={mode === "edit" ? "Edit data collection settings" : "Set up data collection"}
                description={mode === "edit" ? "Modify what information you collect from customers and attendees." : "Configure what customer information you need, then add custom fields."}
            />

            {loading ? (
                <div className="py-10 text-sm text-gray-500">Loading…</div>
            ) : (
                <>
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                        <div className="text-sm font-semibold text-gray-900">Always collected</div>
                        <p className="mt-1 text-sm text-gray-600">
                            We always collect the buyer’s <span className="font-semibold">name</span> and{" "}
                            <span className="font-semibold">email</span> for order delivery, confirmations, and support.
                        </p>
                        <p className="mt-2 text-xs text-gray-500">
                            Use the settings below to collect additional attendee details and custom fields.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <ToggleRow
                            title="Collect attendee details for each ticket"
                            hint="Off = only buyer details (name + email). On = collect per-attendee details for each ticket."
                            checked={opts.collectAttendeeDetailsForEachTicket}
                            onChange={(v) => saveOptions({collectAttendeeDetailsForEachTicket: v})}
                        />
                        <ToggleRow
                            title="Single ticket per order"
                            hint="Customers can only purchase one ticket per order."
                            checked={opts.singleTicketPerOrder}
                            onChange={(v) => saveOptions({singleTicketPerOrder: v})}
                        />
                        <div className="space-y-0">
                            <ToggleRow
                                title="Collect medical aid"
                                hint="Enable Medical Aid section fields during checkout."
                                checked={opts.collectMedicalAid}
                                onChange={(v) => saveOptions({collectMedicalAid: v})}
                            />
                            {opts.collectMedicalAid ? (
                                <TicketScopePicker
                                    label="Collect medical aid"
                                    ticketTypeIds={opts.medicalAidTicketTypeIds}
                                    tickets={ticketOptions}
                                    onChange={(ids) => saveOptions({medicalAidTicketTypeIds: ids})}
                                />
                            ) : null}
                        </div>

                        <div className="space-y-0">
                            <ToggleRow
                                title="Collect emergency contact"
                                hint="Enable Emergency Contact section fields during checkout."
                                checked={opts.collectEmergencyContact}
                                onChange={(v) => saveOptions({collectEmergencyContact: v})}
                            />
                            {opts.collectEmergencyContact ? (
                                <TicketScopePicker
                                    label="Collect emergency contact"
                                    ticketTypeIds={opts.emergencyContactTicketTypeIds}
                                    tickets={ticketOptions}
                                    onChange={(ids) => saveOptions({emergencyContactTicketTypeIds: ids})}
                                />
                            ) : null}
                        </div>

                        <ToggleRow
                            title="Membership lookup required"
                            hint="Require membership lookup before checkout (useful for member-only events)."
                            checked={opts.membershipLookupRequired}
                            onChange={(v) => saveOptions({membershipLookupRequired: v})}
                        />

                        <div className="space-y-0">
                            <ToggleRow
                                title="Waiver required"
                                hint="Customers must accept a waiver before completing purchase."
                                checked={opts.waiverRequired}
                                onChange={(v) => saveOptions({waiverRequired: v})}
                                icon={<ShieldIcon className="size-5"/>}
                            />
                            {opts.waiverRequired ? (
                                <TicketScopePicker
                                    label="Waiver required"
                                    ticketTypeIds={opts.waiverTicketTypeIds}
                                    tickets={ticketOptions}
                                    onChange={(ids) => saveOptions({waiverTicketTypeIds: ids})}
                                />
                            ) : null}
                        </div>
                    </div>

                    {opts.waiverRequired ? (
                        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                            <MarkdownField
                                title="Waiver text"
                                description="Shown during checkout. Keep it clear and specific."
                                value={opts.waiverText ?? ""}
                                onChange={(v) => saveOptions({waiverText: v})}
                            />
                        </div>
                    ) : null}

                    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                        <div
                            className="border-b border-gray-200 bg-gray-50 px-5 py-4 flex items-center justify-between gap-4">
                            <div>
                                <div className="text-sm font-semibold text-gray-900">Custom fields</div>
                                <div className="mt-1 text-xs text-gray-600">Add fields customers must fill in. Drag to
                                    reorder.
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {dirtyOrder ? (
                                    <Button type="button" variant="secondary" size="md" onClick={saveOrder}
                                            disabled={savingOrder}>
                    <span className="inline-flex items-center gap-2">
                      <SaveIcon className="size-4"/>{savingOrder ? "Saving…" : "Save order"}
                    </span>
                                    </Button>
                                ) : null}
                                <Button type="button" variant="primary" size="md" onClick={openNewField}>
                                    <span className="inline-flex items-center gap-2"><PlusIcon className="size-4"/>Add field</span>
                                </Button>
                            </div>
                        </div>

                        <ul className="divide-y divide-gray-100">
                            {opts.customFields.length === 0 ? (
                                <li className="px-5 py-10 text-center text-sm text-gray-500">
                                    No custom fields yet. Click <span className="font-medium">Add field</span>.
                                </li>
                            ) : opts.customFields.map((f, idx) => (
                                <li
                                    key={f.id ?? `new-${idx}`}
                                    draggable
                                    onDragStart={() => onDragStart(idx)}
                                    onDragOver={onDragOver}
                                    onDrop={() => onDrop(idx)}
                                    className="px-5 py-4 hover:bg-gray-50/60"
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex items-start gap-3 min-w-0">
                                            <div className="mt-0.5 flex items-center gap-2 text-gray-500">
                                                <GripVerticalIcon className="size-4"/>
                                                <span className="text-xs">{f.position}</span>
                                            </div>

                                            <div className="min-w-0">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <div
                                                        className="truncate text-sm font-semibold text-gray-900">{f.label}</div>
                                                    <span
                                                        className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-700 ring-1 ring-gray-200">{groupLabel(f.section)}</span>
                                                    <span
                                                        className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700 ring-1 ring-indigo-100">{typeLabel(f.type)}</span>
                                                    {f.required ? (
                                                        <span
                                                            className="rounded-full bg-orange-50 px-2 py-0.5 text-[11px] font-semibold text-orange-700 ring-1 ring-orange-100">
                              Required
                            </span>
                                                    ) : null}
                                                    <span
                                                        className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-100">
                                                        {f.ticketTypeIds.length === 0
                                                            ? "All tickets"
                                                            : `${f.ticketTypeIds.length} ticket type${f.ticketTypeIds.length === 1 ? "" : "s"}`}
                                                    </span>
                                                </div>
                                                {f.description ? <div
                                                    className="mt-1 line-clamp-1 text-xs text-gray-500">{f.description}</div> : null}
                                                {f.type === "DROPDOWN" && f.options ? (
                                                    <div
                                                        className="mt-1 text-xs text-gray-500">Options: {f.options}</div>
                                                ) : null}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 shrink-0">
                                            <button
                                                type="button"
                                                onClick={() => openEditField(f)}
                                                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                                            >
                                                <PencilIcon className="size-4"/>
                                                <span className="hidden sm:inline">Edit</span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => deleteField(f)}
                                                className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-50"
                                            >
                                                <Trash2Icon className="size-4"/>
                                                <span className="hidden sm:inline">Delete</span>
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="flex items-center justify-end gap-3">
                        <Button type="button" variant="secondary" size="md"
                                onClick={() => navigate("/business/events")}>
                            Exit
                        </Button>
                        <Button type="button" variant="primary" size="md" onClick={goNext}
                                disabled={savingOptions || savingOrder || savingField}>
                            Continue to Coupons
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
}